import { envConfig, isProd } from "@configs/env";
import { DeployEnv } from "@types";
import { useEffect, useMemo, useState } from "react";

type ServerConfig = {
  underMaintenance: boolean;
};

const fetchMaintenanceConfig = async (url: string): Promise<ServerConfig | null> => {
  const res = await fetch(url, { cache: "no-store" });

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new Error(`Failed to fetch maintenance config: ${res.status}`);
  }

  return (await res.json()) as ServerConfig;
};

export const useUnderMaintenance = () => {
  const [serverConfig, setServerConfig] = useState<ServerConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    if (envConfig.deploymentEnv === DeployEnv.Local) return;
    const group = envConfig.brand.group;
    const accountId = envConfig.accountId;
    const env = envConfig.deploymentEnv;
    const queryToken = new Date().getTime();

    let overrideUnderMaintenance = false;
    if (!isProd() && localStorage.getItem("ENABLE_MAINTENANCE_DEV") === "true") {
      overrideUnderMaintenance = true;
    }

    const loadMaintenanceConfig = async () => {
      try {
        const groupConfig = await fetchMaintenanceConfig(
          `https://raw.githubusercontent.com/99techteam/kz-config/main/groups/${group}/${env}.json?t=${queryToken}`,
        );

        if (groupConfig?.underMaintenance) {
          setServerConfig({
            underMaintenance: true,
          });
          return;
        }

        const accountConfig = await fetchMaintenanceConfig(
          `https://raw.githubusercontent.com/99techteam/kz-config/main/groups/${group}/${accountId}.${env}.json?t=${queryToken}`,
        );

        setServerConfig({
          underMaintenance: overrideUnderMaintenance || !!accountConfig?.underMaintenance,
        });
      } catch {
        setServerConfig(null);
      } finally {
        setIsLoading(false);
      }
    };

    void loadMaintenanceConfig();
  }, []);

  const underMaintenance = useMemo(() => {
    return serverConfig?.underMaintenance ?? import.meta.env.VITE_MAINTENANCE === "true";
  }, [serverConfig]);

  return { underMaintenance, isLoading };
};
